import { useCallback, useEffect, useState } from 'react'
import { getTreeDetails } from '@fs/zion-tree-groups'

/**
 * Fetches tree details and exposes refetch function; exposes error for potential future handling.
 *
 * `loading` lets a caller tell "the details have not arrived yet" from "the details carry nothing",
 * which is the difference between showing a placeholder and showing an empty cell.
 *
 * @param {import('@fs/zion-tree-types').TreeId | undefined} treeId
 * @returns {{treeDetails: import('@fs/zion-tree-types').TreeDetails | undefined, error: Error | undefined, loading: boolean, refetchTreeDetails: () => void}}
 */

export function useTreeDetails(treeId) {
  const [treeDetails, setTreeDetails] = useState()
  const [error, setError] = useState()
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  // Prevent first-render gap: when treeId is present, initialize loading to true so
  // placeholder shows immediately. Starting false would show an empty cell on first render.

  const [loading, setLoading] = useState(Boolean(treeId))

  const refetchTreeDetails = useCallback(() => {
    setRefetchTrigger((prev) => prev + 1)
  }, [])

  useEffect(() => {
    let isCurrent = true
    async function fetchDetails() {
      setLoading(true)
      try {
        const ignoreCache = refetchTrigger > 0
        const data = await getTreeDetails(treeId, ignoreCache)
        if (isCurrent) setTreeDetails(data)
      } catch (err) {
        if (isCurrent) setError(err)
      } finally {
        if (isCurrent) setLoading(false)
      }
    }
    if (treeId) {
      fetchDetails()
    } else {
      setLoading(false)
    }
    return () => {
      isCurrent = false
    }
  }, [treeId, refetchTrigger])

  return { treeDetails, error, loading, refetchTreeDetails }
}
